<template>
  <div v-if="loading">Cargando...</div>

  <PageHeader :breadcrum-routes="routes" title="Movimiento de integración">
    <template #tags>
      <IntegrationMovementStateTag
        :state-id="movement.stateId"
        :state-tag="movement.stateTag"
      />
      <IntegrationMovementTypeTag
        :type-id="movement.typeId"
        :type-tag="movement.typeTag"
      />
    </template>

    <template v-if="userRole != 'rrhh'" #buttons>
      <IntegrationMovementActions
        :movement-state="movement.stateId"
        @set-as-managed="setAsManaged"
        @set-as-not-considered="setAsNotConsidered"
      />
    </template>
  </PageHeader>

  <IntegrationMovementDetails
    :movement="movement"
    :state-logs="movement.stateLogs"
    :employee="movement.integrationEmployee"
    :payment-method="movement.paymentMethod"
    :company="movement.company"
  />

  <ActionCommentModal
    v-model:visible="showModal"
    :modal-config="currentModalConfig"
    @confirm="handleConfirm"
  />
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import {
  GET_LOGGED_USER,
  INTEGRATION_MOVEMENTS_FETCH_INTEGRATION_MOVEMENT,
  INTEGRATION_MOVEMENTS_GET_ERROR,
  INTEGRATION_MOVEMENTS_GET_INTEGRATION_MOVEMENT,
  INTEGRATION_MOVEMENTS_GET_LOADING,
  INTEGRATION_MOVEMENTS_SET_INTEGRATION_MOVEMENT_AS_MANAGED,
  INTEGRATION_MOVEMENTS_SET_INTEGRATION_MOVEMENT_AS_NOT_CONSIDERED
} from '@/store/types';
import PageHeader from '@/components/page-header.vue';
import IntegrationMovementStateTag from '@/new-components/integration/movements/integration-movement-state-tag.vue';
import IntegrationMovementTypeTag from '@/new-components/integration/movements/integration-movement-type-tag.vue';
import IntegrationMovementActions from '@/new-components/integration/movements/integration-movement-actions.vue';
import IntegrationMovementDetails from '@/new-components/integration/movements/integration-movement-details.vue';
import ActionCommentModal from '@/bewell-components/modals/action-comment-modal.vue';
import notification from '@/utils/notifications';

const store = useStore();
const route = useRoute();

const showModal = ref(false);
const currentModalConfig = ref({});
const currentAction = ref('');

const modalConfigs = {
  managed: {
    title: '¿Estás seguro que deseas marcar el movimiento como gestionado?',
    commentLabel: 'Comentario (opcional)',
    placeholder: 'Ingresa las observaciones del movimiento gestionado...',
    required: false,
    confirmText: 'Marcar como gestionado',
    cancelText: 'Cancelar'
  },
  notConsidered: {
    title: '¿Estás seguro que deseas marcar el movimiento como no considerado?',
    commentLabel: 'Comentario (requerido)',
    placeholder:
      'Describe el motivo por el cual no se considera este movimiento...',
    required: true,
    confirmText: 'Marcar como no considerado',
    cancelText: 'Cancelar',
    errorMessage: 'Debes especificar porque no se considera este movimiento'
  }
};

const userRole = store.getters[GET_LOGGED_USER].roleName;

const routes = computed(() => [
  {
    path: '/integraciones/movimientos',
    breadcrumbName: 'Movimientos de integración'
  },
  {
    breadcrumbName: route.params.id
  }
]);

const movement = computed(
  () => store.getters[INTEGRATION_MOVEMENTS_GET_INTEGRATION_MOVEMENT]
);
const loading = computed(
  () =>
    store.getters[INTEGRATION_MOVEMENTS_GET_LOADING].fetchIntegrationMovement
);
const fetchingMovementsError = computed(
  () => store.getters[INTEGRATION_MOVEMENTS_GET_ERROR].fetchIntegrationMovement
);
const settingAsManagedError = computed(
  () =>
    store.getters[INTEGRATION_MOVEMENTS_GET_ERROR]
      .setIntegrationMovementAsManaged
);
const settingAsNotConsideredError = computed(
  () =>
    store.getters[INTEGRATION_MOVEMENTS_GET_ERROR]
      .setIntegrationMovementAsNotConsidered
);

watch(fetchingMovementsError, (newError) => {
  if (newError) {
    notification('error', 'Error', newError);
  }
});

watch(settingAsManagedError, (newError) => {
  if (newError) {
    notification('error', 'Error', newError);
  }
});

watch(settingAsNotConsideredError, (newError) => {
  if (newError) {
    notification('error', 'Error', newError);
  }
});

const setAsManaged = () => {
  currentAction.value = 'managed';
  currentModalConfig.value = modalConfigs.managed;
  showModal.value = true;
};

const setAsNotConsidered = () => {
  currentAction.value = 'notConsidered';
  currentModalConfig.value = modalConfigs.notConsidered;
  showModal.value = true;
};

const handleConfirm = async (comment, callback) => {
  try {
    if (currentAction.value === 'managed') {
      await store.dispatch(
        INTEGRATION_MOVEMENTS_SET_INTEGRATION_MOVEMENT_AS_MANAGED,
        {
          id: route.params.id,
          comment
        }
      );
    } else if (currentAction.value === 'notConsidered') {
      await store.dispatch(
        INTEGRATION_MOVEMENTS_SET_INTEGRATION_MOVEMENT_AS_NOT_CONSIDERED,
        {
          id: route.params.id,
          comment
        }
      );
    }
    store.dispatch(INTEGRATION_MOVEMENTS_FETCH_INTEGRATION_MOVEMENT, {
      id: route.params.id
    });
    callback({ close: true });
  } catch {
    callback({ close: false });
  }
};

onMounted(() => {
  store.dispatch(INTEGRATION_MOVEMENTS_FETCH_INTEGRATION_MOVEMENT, {
    id: route.params.id
  });
});
</script>
